import FormContentLoader from 'components/FormContentLoader'
import LinkButton from 'components/LinkButton'
import ENDPOINT from 'constants/endpoints'
import {ERROR_FIELD} from 'constants/error-fields';
import {Formik} from 'formik'
import {Role} from 'helpers/user';
import {useApi} from 'hooks/useApi';
import cloneDeep from 'lodash/cloneDeep'
import isArray from 'lodash/isArray'
import merge from 'lodash/merge'
import React, {useEffect, useMemo, useState} from 'react'
import {toast} from 'react-toastify'
import BasicDialog from 'scenes/Dialog/components/BasicDialog'
import FormDialog from 'scenes/Dialog/components/FormDialog'
import ResponseErrorDialog from 'scenes/Dialog/components/ResponseErrorDialog'
import AccountChangeEmailForm from 'scenes/Form/components/AccountChangeEmailForm';
import AccountSettingsChangeEmailForm from 'scenes/Form/components/AccountSettingsChangeEmailForm';
import BasicButton from '../../../../../components/BasicButton';
import {AccountSettingsFormValidationSchema} from '../validate'
import './PreferencesSection.css';

const PreferencesSection = props => {
  const {nextEmail, sections, user} = props

  const initData = {
    isFetching: true,
    fields: {
      JobMailingAlternateEmail: '',
      JobMailingAlternateEmailActive: 'No',
      EmailAddress: '',
      confirmPass: '',
      LinkedInURL: '',
    },
    permissions: {
      AllowedChantSettingsGoOffline: false,
      AllowedChangeEmail: false,
      AllowedChantSettingsEdit: false,
      AllowedPersonalCalendar: false,
      AllowedRecruitersProcessDigest: false,
    },
  };

  const api = useApi();
  const [formData, setFormData] = useState(initData);
  const {isCandidate, isRecruiter} = useMemo(() => (
    {
      isCandidate: Role.isCandidate(user),
      isRecruiter: Role.isRecruiter(user),
    }
  ), [user]);

  useEffect(() => {
    api.get(
      ENDPOINT.FORM.GET.ACCOUNT_SETTINGS,
      {},
      {
        handleError: response => ResponseErrorDialog({response}),
        handleSuccess: formData => {
          return setFormData({
            ...merge(initData, formData),
            isFetching: false,
          })
        },
      }
    );
  }, []);

  useEffect(() => {
    const nextData = cloneDeep(formData);
    nextData.fields.EmailAddress = nextEmail;
    nextData.fields.changeEmailRequestTo = '';
    setFormData(nextData);
  }, [nextEmail]);

  const scrollToError = () => {
    if (document.getElementsByClassName('form__error').length > 0) {
      const elem = document.getElementsByClassName('form__error')[0];
      if (elem.previousElementSibling) {
        elem.previousElementSibling.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'start',
        });
      }
    }
  };

  const onSubmit = (values, {resetForm, setSubmitting, setErrors}) => {
    const preparedValues = cloneDeep(values);
    Object.keys(preparedValues).forEach(field => {
      let data = preparedValues[field];
      if (
        preparedValues[field] === null ||
        field === 'changeEmailRequestTo' ||
        field === 'EmailAddress'
      ) {
        delete preparedValues[field];
      } else if (isArray(data) && data.length === 0) {
        delete preparedValues[field];
      }
    });
    const apiCall = new Promise((resolve, reject) => {
      api.post(ENDPOINT.FORM.SAVE.ACCOUNT_SETTINGS, preparedValues, {
        handleError: response => {
          reject(response)
          onSubmitError(response, setErrors)
        },
        handleSuccess: (response) => {
          resolve(response)
          resetForm({...values});
        },
        handleComplete: () => setSubmitting(false),
      });
    });
    return toast.promise(apiCall, {
      pending: {
        render() {
          return <p>Saving...</p>
        },
        icon: false,
      },
      success: {
        render() {
          return <p>Your account data has been updated.</p>
        },
        // other options
        icon: "🟢",
      },
      error: {
        render({data}) {
          const {
            data: {fields},
          } = data;
          if (fields && typeof setErrors === 'function') {
            const obj = {};
            for (let [key, value] of Object.entries(fields)) {
              const error = isArray(value) ? value[0] : value;
              obj[key] = ERROR_FIELD[error] || `${error}`;
            }
            return Object.values(obj).join('<br />')
          } else {
            // When the promise reject, data will contains the error
            return `${data.status}`
          }
        }
      }
    });
  };

  const onSubmitError = (response, setErrors) => {
    const {
      data: {fields},
    } = response;
    if (fields && typeof setErrors === 'function') {
      const obj = {};
      for (let [key, value] of Object.entries(fields)) {
        const error = isArray(value) ? value[0] : value;
        obj[key] = ERROR_FIELD[error] || `${error}`;
      }
      setErrors(obj);
      scrollToError();
    } else {
      ResponseErrorDialog({response});
    }
  };

  const handleChangeEmail = setFieldValue => {
    FormDialog({
      title: 'Change Login Email',
      compact: true,
      popupStyles: {minHeight: 'auto'},
      form: (actions, params) => (
        <AccountChangeEmailForm actions={actions} params={params}/>
      ),
    }).then(
      response => {
        setFieldValue('changeEmailRequestTo', response.email);
        BasicDialog({bodyRender: <p>Please, confirm request by email</p>});
      },
      response => ResponseErrorDialog({response})
    );
  };

  const handleChangeEmailCancel = ({setFieldValue, setSubmitting}) => {
    setSubmitting(true);
    api.post(
      ENDPOINT.ACTION.ACCOUNT.CHANGE_EMAIL_CANCEL,
      {},
      {
        handleError: response => ResponseErrorDialog({response}),
        handleSuccess: () => setFieldValue('changeEmailRequestTo', null),
        handleComplete: () => setSubmitting(false),
      }
    );
  };

  const handleChangeAlternateEmail = (values, title, {onSubmit, setFieldValue, resetForm, setSubmitting, setErrors}) => {
    return FormDialog({
      title: title,
      compact: true,
      popupStyles: {minHeight: 'auto'},
      form: (actions, params) => (
        <AccountSettingsChangeEmailForm actions={actions} params={{...params, email: ''}}/>
      ),
    }).then((response) => {
        onSubmit({
          ...values,
          JobMailingAlternateEmailActive: response.email.trim() !== '' ? 'Yes' : 'No',
          JobMailingAlternateEmail: response.email,
        }, {resetForm, setSubmitting, setErrors})
          .catch(() => {
            setFieldValue('JobMailingAlternateEmail', values.JobMailingAlternateEmail);
            setFieldValue('JobMailingAlternateEmailActive', values.JobMailingAlternateEmailActive);
          });
      },
      response => ResponseErrorDialog({response})
    )
  }

  const allowedChangeEmail = formData.permissions.AllowedChangeEmail;
  const processDigestAlternateEmail = formData.fields ? formData.fields.ProcessDigestAlternateEmail || formData.fields.EmailAddress : '';
  const alternateEmail = formData.fields ? formData.fields.AlternateEmail || formData.fields.EmailAddress : '';

  return (
    <Formik
      enableReinitialize
      initialValues={formData.fields}
      validationSchema={AccountSettingsFormValidationSchema}
      onSubmit={onSubmit}
    >
      {({
          isValidating,
          values,
          setFieldValue,
          submitForm,
          setSubmitting,
          resetForm,
          setErrors
        }) => (
        <form
          onSubmit={e => {
            e.preventDefault();
            submitForm().then(() =>
              isValidating ? scrollToError() : null
            );
          }}
          className="form__content form__content--full-width"
          autoComplete="off"
        >
          <div className="form__header"><a name="preferences"></a>
            <h4 className="form__title">{sections['preferences'].caption}</h4>
          </div>
          {formData.isFetching && <FormContentLoader/>}
          <div className="form__group">
            {!formData.isFetching && (
              <div className={'account-pref'}>
                <div className="account-pref__line">
                  <h3 className={'account-pref__title'}>Login Email</h3>
                  <div className={'account-pref__value'}>
                    {values.changeEmailRequestTo ? (
                      <>
                        <span>{values.EmailAddress}</span>
                        <div className={'account-pref__description'}>
                          {`(Change request to: ${values.changeEmailRequestTo})`}
                          <br/>
                          <LinkButton
                            text="Cancel"
                            controlFunc={() =>
                              handleChangeEmailCancel({
                                setFieldValue,
                                setSubmitting,
                              })
                            }
                          />
                        </div>
                      </>
                    ) : values.EmailAddress}
                  </div>
                  <div className={'account-pref__action'}>
                    {allowedChangeEmail && (
                      <BasicButton
                        btnArrow={false}
                        btnText="Change"
                        btnWidth={'auto'}
                        btnHeight={24}
                        controlFunc={() => handleChangeEmail(setFieldValue)}
                      />
                    )}
                  </div>
                </div>

                {isCandidate && (
                  <div className="account-pref__line">
                    <h3 className={'account-pref__title'}>
                      {isRecruiter ? 'Platform & Job Notifications' : 'Job Opportunities'}
                    </h3>
                    <div className={'account-pref__value'}>
                      {
                        values.JobMailingAlternateEmailActive === 'Yes'
                          ? values.JobMailingAlternateEmail
                          : values.EmailAddress
                      }
                    </div>
                    <div className={'account-pref__action'}>
                      <BasicButton
                        btnArrow={false}
                        btnText="Change"
                        btnWidth={'auto'}
                        btnHeight={24}
                        controlFunc={() => {
                          handleChangeAlternateEmail(
                            values,
                            isRecruiter ? 'Change email for Platform & Job Notifications' : 'Change Email for Job Opportunities',
                            {
                              onSubmit,
                              setFieldValue,
                              resetForm,
                              setSubmitting,
                              setErrors,
                            })
                        }}
                      />
                    </div>
                  </div>
                )}
                {isRecruiter && (
                  <div className="account-pref__line">
                    <h3 className={'account-pref__title'}>(Recruiter) Inbound Applications</h3>
                    <div className={'account-pref__value'}>
                      <div>{processDigestAlternateEmail}</div>
                      {alternateEmail !== processDigestAlternateEmail && (
                        <span>
                          {alternateEmail}
                        </span>
                      )}
                    </div>
                    <div className={'account-pref__action'}>
                      <LinkButton to={'/candidate/account?expand=1#recruiter-notifications'} text={'Manage Alerts & Schedule'}/>
                    </div>
                  </div>
                )}

                <input type={'hidden'} value={values.LinkedInURL}/>
              </div>
            )}
          </div>
        </form>
      )}
    </Formik>
  );
}

export default PreferencesSection;
